import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { FollowUpLetterDto, FollowUpLetterFile, FulParticipant } from "../../types/Rows";
import ScientiaHeader from "../../components/ScientiaHeader";
import { ROBOTO_SOURCES } from "../report/document/pdfFonts";
import { Footer } from "./footer";

// Te same pliki Roboto co raport (lokalnie, nie z CDN); nazwa rodziny bez zmian
Font.register({ family: "Times New Roman", fonts: ROBOTO_SOURCES });

const styles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    fontSize: 12,
    paddingBottom: 65,
    paddingHorizontal: 35,
    backgroundColor: "white",
  },
  section: {
    marginBottom: 5,
    marginTop: 5,
    fontSize: 10,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "semibold",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
  pageParagraph: {
    fontSize: 10,
    fontWeight: "normal",
  },
  dateContainer: {
    fontWeight: "bold",
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "flex-end",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCell: {
    padding: 5,
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#bfbfbf",
    fontSize: 10,
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
  },
  flexTable: {
    display: "flex",
    flexDirection: "column",
    marginVertical: 10,
    borderWidth: 1,
    borderColor: "#bfbfbf",
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#bfbfbf",
  },
  flexCell: {
    flex: 1,
    padding: 5,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
    borderColor: "#bfbfbf",
  },
  flexHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
  },
  lastCell: {
    borderRightWidth: 0,
  },
});
const getRadioResult = (val?: string) => {
  if (val === "yes") return "TAK";
  else if (val === "no") return "NIE";
  else if (val === "na") return "NA";
  else return " ";
};

const formatDate = (date: Date) => {
  const d = new Date(date);
  let day = d.getDate().toString().padStart(2, "0");
  let month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

const fmtNullable = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  return formatDate(new Date(iso));
};

export const FUL_SECTIONS: { key: string; label: string }[] = [
  { key: "header",          label: "Nagłówek (numer listu, protokół, sponsor)" },
  { key: "introduction",    label: "Tekst wprowadzający" },
  { key: "participants",    label: "Uczestnicy CRO i ośrodka" },
  { key: "study-progress",  label: "Przegląd zagadnień" },
  { key: "status-osrodka",  label: "Status Ośrodka" },
  { key: "patient-list",    label: "Lista pacjentów (status)" },
  { key: "podsumowanie",    label: "Podsumowanie" },
  { key: "signature",       label: "Podpis" },
  { key: "attachments",     label: "Załączniki" },
];

const getBoolText = (val: boolean) => (val ? "TAK" : "NIE");

export const FollowUpLetterDocGenerator = ({
  followUpLetter,
  author,
  footerText,
  versionText,
  sections,
  attachments = [],
  participants = [],
}: {
  followUpLetter: FollowUpLetterDto;
  author: string;
  footerText?: string;
  versionText?: string;
  sections?: Set<string>;
  attachments?: FollowUpLetterFile[];
  participants?: FulParticipant[];
}) => {
  const show = (key: string) => !sections || sections.has(key);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View fixed>
          <ScientiaHeader />
        </View>
        <View style={styles.dateContainer}>
          <Text>Data: {formatDate(new Date())}</Text>
        </View>

        {show("header") && (
          <>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                List follow up nr: {followUpLetter.followUpLetterNumber}
                {followUpLetter.protocolNumber || followUpLetter.studyAcronym
                  ? ` — ${followUpLetter.protocolNumber ?? ""}${followUpLetter.studyAcronym ? ` (${followUpLetter.studyAcronym})` : ""}`
                  : ""}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                Sponsor: {followUpLetter.sponsor}
              </Text>
            </View>
          </>
        )}

        {show("introduction") && (
          <View style={styles.section}>
            <View style={styles.section}>
              <Text>{followUpLetter.introductionText}</Text>
            </View>
            <Text>
              Serdecznie dziękuję Państwu za udział w wizycie{" "}
              {followUpLetter.visitType} która odbyła się{" "}
              {formatDate(new Date(followUpLetter.visitDate))} oraz przygotowanie
              dokumentacji do weryfikacji.
            </Text>
          </View>
        )}

        {show("participants") && (
          <>
            <View style={styles.section}>
              <Text style={styles.pageParagraph}>Uczestnicy CRO:</Text>
              <Text>{followUpLetter.croParticipants}</Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.pageParagraph}>Uczestnicy ośrodka:</Text>
              <Text>{followUpLetter.resortParticipants}</Text>
            </View>
          </>
        )}

        {show("study-progress") && (
          <View style={styles.section}>
            <Text style={styles.pageParagraph}>
              Podczas wizyty omówiono/ zweryfikowano następujące zagadnienia:
            </Text>
            {followUpLetter.studyProgress?.trim() ? (
              <Text>{followUpLetter.studyProgress}</Text>
            ) : (
              // SR-042: odpowiedzi „Follow up” trafiają do PDF tylko raz — tutaj,
              // gdy monitor nie wciągnął ich do pola „Postęp badania”.
              followUpLetter?.answers?.map((answer, index) => (
                <View key={index} style={styles.section}>
                  <Text style={styles.pageParagraph}>
                    {index + 1}. {answer.description}
                  </Text>
                  <Text style={styles.pageParagraph}>
                    - {getRadioResult(answer.answer)}
                    {". "}
                    {answer.comment}
                  </Text>
                </View>
              ))
            )}
          </View>
        )}

        {show("status-osrodka") && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Status Ośrodka:</Text>
            <View style={styles.flexTable}>
              <View style={[styles.flexRow, styles.flexHeader]}>
                <Text style={[styles.flexCell, { flex: 2, textAlign: "left" }]}>Parametr</Text>
                <Text style={[styles.flexCell, styles.lastCell]}>Liczba</Text>
              </View>
              {[
                ["Zadeklarowani uczestnicy", followUpLetter.declaredParticipants],
                ["W screeningu", followUpLetter.amountForScreening],
                ["Zrandomizowani", followUpLetter.amountOfRandomized],
                ["Niepowodzenia screeningu", followUpLetter.screenFailure],
                ["W trakcie leczenia", followUpLetter.duringTreatment],
                ["Odchylenia od protokołu", followUpLetter.protocolDeviation],
                ["Zdarzenia SAE", followUpLetter.reportedSAE],
              ].map(([label, value], i) => (
                <View key={i} style={styles.flexRow}>
                  <Text style={[styles.flexCell, { flex: 2, textAlign: "left" }]}>{label}</Text>
                  <Text style={[styles.flexCell, styles.lastCell]}>{value ?? 0}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {show("patient-list") && participants.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Zakres monitorowanych danych:</Text>
            <View style={[styles.flexTable, { marginTop: 4 }]}>
              <View style={[styles.flexRow, styles.flexHeader]}>
                <Text style={styles.flexCell}>Nr uczestnika</Text>
                <Text style={styles.flexCell}>Nr zweryfikowanej wizyty</Text>
                <Text style={styles.flexCell}>100% SDV</Text>
                <Text style={styles.flexCell}>RBV</Text>
                <Text style={[styles.flexCell, styles.lastCell, { flex: 2 }]}>Komentarz</Text>
              </View>
              {/* SR-005: jak w raporcie (SR-021) — komentarz w wierszu uczestnika, nie lista pod tabelą */}
              {participants.map((p, i) => (
                <View key={i} style={styles.flexRow} wrap={false}>
                  <Text style={styles.flexCell}>{i + 1}. {p.participantNumber}</Text>
                  <Text style={styles.flexCell}>{p.visitNumber}</Text>
                  <Text style={styles.flexCell}>{getBoolText(p.isFinshed)}</Text>
                  <Text style={styles.flexCell}>{getBoolText(p.isVerified)}</Text>
                  <Text style={[styles.flexCell, styles.lastCell, { flex: 2, textAlign: "left" }]}>
                    {p.comment ?? ""}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {show("podsumowanie") && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Podsumowanie:</Text>
            {followUpLetter.finalComment && (
              <Text style={[styles.pageParagraph, { marginBottom: 6 }]}>
                {followUpLetter.finalComment}
              </Text>
            )}
          </View>
        )}

        {show("signature") && (
          <View style={styles.section}>
            <Text>Z poważaniem,</Text>
            <Text>{author}</Text>
          </View>
        )}

        {show("attachments") && attachments.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Załączniki:</Text>
            {attachments.map((f, i) => (
              <Text key={f.id} style={styles.pageParagraph}>
                {i + 1}. {f.name}
              </Text>
            ))}
          </View>
        )}

        <Footer footerText={footerText} versionText={versionText} />
      </Page>
    </Document>
  );
};
